import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Zap, LogIn, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const BG =
  "https://images.pexels.com/photos/10410202/pexels-photo-10410202.jpeg";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  if (user && user !== null && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(username.trim(), password);
    setLoading(false);
    if (ok) navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-background/85 backdrop-blur-sm" aria-hidden />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="font-manrope font-extrabold text-2xl leading-none">VTC Lyon</div>
            <div className="overline mt-1">ERP Chauffeur • Micro-entrepreneur</div>
          </div>
        </div>

        <Card className="border-border/60 backdrop-blur-xl bg-card/90">
          <CardHeader>
            <CardTitle className="font-manrope">Connexion</CardTitle>
            <CardDescription>
              Accédez à votre tableau de bord VTC.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="username">Identifiant</Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="chauffeur"
                  data-testid="input-username"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="input-password"
                />
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/25 rounded-md px-3 py-2" data-testid="auth-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full active:scale-[0.98]"
                disabled={loading}
                data-testid="btn-submit-auth"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><LogIn className="h-4 w-4 mr-2" /> Se connecter</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
